import React, { useState, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil, faTrash } from "@fortawesome/free-solid-svg-icons";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Table,
  CardHeader,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Spinner,
  Badge,
  Input,
} from "reactstrap";
import { Link } from "react-router-dom";
import MetaTags from "react-meta-tags";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ApiClient from "../BaseUrl/ApiClient";
import axios from "axios";
import PlanSelectionModal from "../Components/PlanSelectionModal";

// Debounce utility
const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

function DealList() {
  const token = localStorage.getItem("accessToken");

  const [subscriptionDetails, setSubscriptionDetails] = useState({
    type: "free",
    method: "Not available",
    hasPaymentCard: false,
  });
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showUpgradeBanner, setShowUpgradeBanner] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalConfig, setModalConfig] = useState({
    skipPlanSelection: false,
    selectedPlan: null,
    action: "upgrade",
  });

  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    totalDocs: 0,
    limit: 10,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState(null);

  const planName = subscriptionDetails.type || "free";

  // Get Deal Limit Based on Plan
  const getDealLimit = () => {
    if (subscriptionLoading) return 4; // Default safe limit during loading
    switch (planName) {
      case "standard":
      case "sponsored":
        return 10;
      default:
        return 4; // Free plan
    }
  };

  // Fetch Subscription
  const fetchSubscription = async (token) => {
    try {
      const { data } = await axiosInstance.get("/business/payment/subscription", {
        headers: { Authorization: `Bearer ${token}` },
      });

      const sub = data?.responseData?.subscription;
      if (!sub) {
        setSubscriptionDetails({
          type: "free",
          method: "Not available",
          hasPaymentCard: false,
        });
        return;
      }

      setSubscriptionDetails({
        type: data?.responseData?.subscriptionType || "standard",
        method: sub.subscriptionMethod || "Not available",
        hasPaymentCard: !!sub.paymentCard,
      });
    } catch (err) {
      setSubscriptionDetails({
        type: "free",
        method: "Not available",
        hasPaymentCard: false,
      });
    } finally {
      setSubscriptionLoading(false);
      setIsRefreshing(false);
    }
  };

  // Fetch Deals
  const fetchDeals = async (page = 1, limit = pagination.limit, query = searchQuery) => {
    try {
      setLoading(true);
      const client = ApiClient();
      const { data } = await client.get("business/deal/dailySpecials/list", {
        params: { searchParam: query, limit, page },
      });

      const fetchedDeals = data.responseData.deals || [];
      const paginationData = data.responseData.pagination || pagination;
      setDeals(fetchedDeals);
      setPagination(paginationData);

      // ✅ Show banner only when limit reached
      const limitReached = fetchedDeals.length >= getDealLimit();
      setShowUpgradeBanner(limitReached);
    } catch {
      toast.error("Failed to fetch deals");
    } finally {
      setLoading(false);
    }
  };

  // Debounced Fetch
  const debouncedFetchDeals = useCallback(
    debounce((page, limit, query) => {
      fetchDeals(page, limit, query);
    }, 500),
    [pagination.limit]
  );

  useEffect(() => {
    if (token) {
      fetchSubscription(token);
      fetchDeals();
    }
  }, [token]);

  const handleSearchInputChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    debouncedFetchDeals(1, pagination.limit, query);
  };

  const clearSearch = () => {
    setSearchQuery("");
    fetchDeals(1, pagination.limit, "");
  };

  const handlePageChange = (page) => fetchDeals(page, pagination.limit, searchQuery);

  const handleLimitChange = (e) => {
    const newLimit = Number(e.target.value);
    setPagination({ ...pagination, limit: newLimit });
    fetchDeals(1, newLimit, searchQuery);
  };

  // Activate / Deactivate
  const toggleActiveStatus = async (dealId, currentStatus) => {
    try {
      setLoading(true);
      const client = ApiClient();
      const { data } = await client.put(`business/deal/dailySpecials/${dealId}/${!currentStatus}`);
      const updatedDeal = data?.responseData?.deal;

      if (updatedDeal) {
        setDeals((prev) =>
          prev.map((d) => (d._id === dealId ? { ...d, isActive: updatedDeal.isActive } : d))
        );
        toast.success(
          `Deal ${updatedDeal.isActive ? "activated" : "deactivated"} successfully`
        );
      }
    } catch {
      toast.error("Failed to update deal status");
    } finally {
      setLoading(false);
    }
  };

  // Delete Deal
  const confirmDelete = (deal) => {
    setSelectedDeal(deal);
    setDeleteModal(true);
  };

const deleteDeal = async () => {
  try {
    const client = ApiClient();
    await client.delete(`business/deal/dailySpecials/${selectedDeal._id}`);

    // Update deals after deletion
    const updatedDeals = deals.filter((d) => d._id !== selectedDeal._id);
    setDeals(updatedDeals);
    toast.success("Deal deleted successfully");

    // ✅ Hide upgrade banner if total deals are below plan limit
    if (updatedDeals.length < getDealLimit()) {
      setShowUpgradeBanner(false);
    }
  } catch {
    toast.error("Failed to delete deal");
  } finally {
    setDeleteModal(false);
    setSelectedDeal(null);
  }
};


  // Handle Banner Button Click
  const handleBannerButtonClick = () => {
    const { type, hasPaymentCard } = subscriptionDetails;

    const config = {
      skipPlanSelection: false,
      selectedPlan: null,
      action: type !== "free" ? "changePlan" : "upgrade",
      skipCardDetails: type !== "free" && hasPaymentCard,
    };

    setModalConfig(config);
    setIsModalOpen(true);
    localStorage.setItem("redirectAfterSubscribe", window.location.pathname);
  };

  // Handle Add Deal Button Click
  const handleAddDealClick = (e) => {
    if (deals.length >= getDealLimit()) {
      e.preventDefault();
      setShowUpgradeBanner(true);
    }
  };

  return (
    <div className="page-content">
      <Container fluid className="mt-5">
        <MetaTags>
          <title>Deals List</title>
        </MetaTags>
        <ToastContainer />

        {/* Delete Confirmation Modal */}
        <Modal isOpen={deleteModal} centered>
          <ModalHeader>Confirm Delete</ModalHeader>
          <ModalBody>
            {selectedDeal && (
              <>
                <p>
                  Do you really want to delete the deal{" "}
                  <strong>"{selectedDeal.name}"</strong>?
                </p>
                <p className="text-danger">This action cannot be undone.</p>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="danger" onClick={deleteDeal}>
              Yes, Delete
            </Button>
            <Button color="secondary" onClick={() => setDeleteModal(false)}>
              Cancel
            </Button>
          </ModalFooter>
        </Modal>

        {/* Subscription Modal */}
        <PlanSelectionModal
          isOpen={isModalOpen && !isRefreshing}
          toggle={() => !isRefreshing && setIsModalOpen(!isModalOpen)}
          isUpgrade={planName !== "free"}
          onSubscriptionSuccess={() => {
            setShowUpgradeBanner(false);
            setIsModalOpen(false);
            fetchDeals();
          }}
          modalConfig={modalConfig}
        />

        <Row>
          <Col xl={12}>
            <Card>
              <CardHeader>
                <Row>
                  <Col md={6}>
                    <h4 className="card-title mb-0">Daily Specials</h4>
                  </Col>
                </Row>
              </CardHeader>

              {/* ✅ Fixed Banner (appears only when limit reached) */}
              {!subscriptionLoading && showUpgradeBanner && (
                <div className="alert alert-warning d-flex justify-content-between align-items-center mt-3">
                  <span>
                    {planName === "free" ? (
                      <>You've reached the <b>Free</b> plan limit (4 deals). Upgrade to add more.</>
                    ) : planName === "standard" ? (
                      <>You can add up to 10 deals. Upgrade to <b>Sponsored</b> for more.</>
                    ) : null}
                  </span>
                  <Button
                    color="primary"
                    size="sm"
                    onClick={handleBannerButtonClick}
                    disabled={isRefreshing}
                  >
                    {planName === "free" ? "Add Plan" : "Upgrade Plan"}
                  </Button>
                </div>
              )}

              <CardBody>
                <Row className="g-4 mb-3">
                  <Col md={4}>
                    <h3>Deals List</h3>
                  </Col>
                  <Col md={8}>
                    <div className="d-flex justify-content-end align-items-center position-relative mb-3">
                      <Link
                        to="/add-daily-special"
                        className="btn btn-success me-2"
                        onClick={handleAddDealClick}
                      >
                        + Add Daily Special Deal
                      </Link>
                      <div className="search-box ms-2 position-relative">
                        <Input
                          type="text"
                          placeholder="Search deals..."
                          value={searchQuery}
                          onChange={handleSearchInputChange}
                        />
                        <i className="ri-search-line search-icon"></i>
                        {searchQuery && (
                          <i
                            className="ri-close-line clear-icon"
                            style={{
                              position: "absolute",
                              right: "10px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              cursor: "pointer",
                              fontSize: "18px",
                              color: "#666",
                            }}
                            onClick={clearSearch}
                          />
                        )}
                      </div>
                    </div>
                  </Col>
                </Row>

                {loading ? (
                  <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "300px" }}>
                    <Spinner color="primary" style={{ width: "3rem", height: "3rem" }} />
                  </div>
                ) : (
                  <div className="table-responsive table-card">
                    <Table className="align-middle table-nowrap mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Deal Photo</th>
                          <th>Name</th>
                          <th>Price</th>
                          <th>Category</th>
                          <th>Status</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {deals.length > 0 ? (
                          deals.map((deal) => (
                            <tr key={deal._id}>
                              <td>
                                {deal.dealPhoto?.url ? (
                                  <img
                                    src={deal.dealPhoto.url}
                                    alt={deal.name}
                                    style={{
                                      width: "60px",
                                      height: "60px",
                                      objectFit: "contain",
                                      borderRadius: "4px",
                                    }}
                                  />
                                ) : (
                                  "No Image"
                                )}
                              </td>
                              <td style={{ maxWidth: "200px", textOverflow: "ellipsis", overflow: "hidden" }}>
                                {deal.name}
                              </td>
                              <td>${deal.price}</td>
                              <td>{deal.category?.name || "N/A"}</td>
                              <td>
                                <Badge color={deal.isActive ? "success" : "danger"}>
                                  {deal.isActive ? "Available" : "Unavailable"}
                                </Badge>
                              </td>
                              <td>
                                <div className="d-flex gap-2">
                                  <button
                                    className={`btn btn-sm ${
                                      deal.isActive ? "btn-danger" : "btn-success"
                                    }`}
                                    onClick={() => toggleActiveStatus(deal._id, deal.isActive)}
                                  >
                                    {deal.isActive ? "Deactivate" : "Activate"}
                                  </button>
                                  <Link to={`/deal/${deal._id}`}>
                                    <button className="btn btn-sm btn-primary">Show Detail</button>
                                  </Link>
                                  <button
                                    className="btn btn-sm btn-danger"
                                    onClick={() => confirmDelete(deal)}
                                  >
                                    <FontAwesomeIcon icon={faTrash} />
                                  </button>
                                  <Link to={`/edit-deal/${deal._id}`}>
                                    <button className="btn btn-sm btn-success">
                                      <FontAwesomeIcon icon={faPencil} />
                                    </button>
                                  </Link>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="6" className="text-center">
                              No deals found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                )}

                {/* Pagination */}
                <div className="d-flex justify-content-end mt-3">
                  <div className="pagination-wrap hstack gap-2">
                    <Link
                      className={`page-item pagination-prev ${
                        pagination.page === 1 ? "disabled" : ""
                      }`}
                      to="#"
                      onClick={() => handlePageChange(pagination.page - 1)}
                    >
                      Previous
                    </Link>
                    <ul className="pagination listjs-pagination mb-0">
                      {Array.from({ length: pagination.totalPages }, (_, i) => (
                        <li
                          key={i}
                          className={`page-item ${pagination.page === i + 1 ? "active" : ""}`}
                        >
                          <Link
                            className="page-link paginationlink"
                            to="#"
                            onClick={() => handlePageChange(i + 1)}
                          >
                            {i + 1}
                          </Link>
                        </li>
                      ))}
                    </ul>
                    <Link
                      className={`page-item pagination-next ${
                        pagination.page === pagination.totalPages ? "disabled" : ""
                      }`}
                      to="#"
                      onClick={() => handlePageChange(pagination.page + 1)}
                    >
                      Next
                    </Link>
                  </div>
                  <select
                    className="form-select ms-2"
                    style={{ width: "100px" }}
                    value={pagination.limit}
                    onChange={handleLimitChange}
                  >
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="50">50</option>
                  </select>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default DealList;
